import { createContext, useContext, useEffect, useRef } from 'react';

// The work a Create screen's steps share: what the earlier steps made is
// what the later ones start from, and the Review step puts it together.
//
// flow: {
//   topic,                    what the post is about (the Hook step's brief, else the planned post's idea)
//   hook, hooks,              picked hook (the last one picked) and every picked hook
//   script,                   { title, hook, beats, cta } from the Script step
//   caption, hashtags,        the caption used for the post, and its hashtags
//   captionPlatform, link,    which platform's caption that is, and its tracked link
//   article,                  { title, metaDescription, body } from the Article step
//   ad,                       the picked ad variant { angle, headline, primaryText, description, cta, placement, limits }
//   image, video,             generated media (data URL / blob URL); not kept across reloads
//   done, skipped,            { [stepId]: true }
// }
export const EMPTY_FLOW = {
    topic: '',
    hook: '',
    hooks: [],
    script: null,
    caption: '',
    hashtags: [],
    captionPlatform: '',
    link: '',
    article: null,
    ad: null,
    image: '',
    imagePrompt: '',
    video: '',
    done: {},
    skipped: {},
};

export const FlowContext = createContext({
    flow: EMPTY_FLOW,
    // Merges fields into the flow
    update: () => {},
    // A step's output: merges `patch` and marks the step done (or not)
    report: () => {},
});

export const useCreateFlow = () => useContext(FlowContext);

// Which step a screen is, and where "Next" goes from it
export const FlowStepContext = createContext({ stepId: '', next: null, goNext: () => {} });
export const useFlowStep = () => useContext(FlowStepContext);

// Fills a field from an earlier step's output, but only while the person
// hasn't typed their own: when the field is empty or still holds the value
// we put there last.
export const usePrefill = (source, value, setValue) => {
    const lastAuto = useRef(value);
    const valueRef = useRef(value);
    valueRef.current = value;
    useEffect(() => {
        if (!source) return;
        const current = valueRef.current;
        const untouched = !String(current || '').trim() || current === lastAuto.current;
        if (untouched && current !== source) setValue(source);
        if (untouched) lastAuto.current = source;
    }, [source, setValue]);
};

// The script as plain text, for copying and for the Review step
export const scriptAsText = (script) => (script ? [
    script.title,
    `Hook: ${script.hook}`,
    ...script.beats.map(beat => `[${beat.time}] ${beat.visual}\nVO: ${beat.voiceover}${beat.onScreenText ? `\nText: ${beat.onScreenText}` : ''}`),
    script.cta ? `CTA: ${script.cta}` : '',
].filter(Boolean).join('\n\n') : '');
